import { MarbleColor } from "../Models/LevelData";
import { CURRENT_SCHEMA_VERSION } from "../Models/Editor/EditorLevel";
import { defaultGeneration } from "./templateOps";
import { ValidationSeverity } from "./validateTemplate";
// A painted slice the editor can actually draw and the backend can actually bind: all three numbers there
// and finite. Anything else names no slice.
function isReadableSlicePaint(paint) {
    if (!isObject(paint)) {
        return false;
    }
    const p = paint;
    return Number.isFinite(p.wedge) && Number.isFinite(p.depth) && Number.isFinite(p.color);
}
// A frozen slice the editor can draw and the backend can bind: both numbers there and finite.
function isReadableFrozenSlice(f) {
    if (!isObject(f)) {
        return false;
    }
    const p = f;
    return Number.isFinite(p.wedge) && Number.isFinite(p.depth);
}
function plural(n, one, many) {
    return n === 1 ? one : many;
}
// A board bound as the backend binds it: a missing or unbindable key arrives there as 0, and 0 is what
// validateTemplate already has an error for ("Board size must be at least 1×1"). Reading it here too keeps
// the number the editor DRAWS with in step with the number it JUDGES — GridCanvas reads `columns` straight
// off the template, so a string or a missing key used to reach the canvas untouched.
function boardBound(v) {
    const n = Math.floor(Number(v));
    return Number.isFinite(n) && n > 0 ? n : 0;
}
function isObject(v) {
    return v !== null && typeof v === "object";
}
// A cell's per-spawn records, materialised the way LevelCompiler.TunnelSpawnFor already reads them: a
// missing array and each null entry fall back to the tunnel cell's own trio. Writing that fallback down
// changes no colour, no queue order and no slice — it only gives the editor's ops something they can read.
//
// The two repairs are NOT the same thing, and the panel has to be able to tell them apart:
//   * no array at all -> an EMPTY one. Nothing is invented, and nothing needs to be: TunnelSpawnFor falls
//     back per index anyway, and inventing `tunnelSpawnCount` records here would mean allocating whatever
//     number a file happens to carry (see MAX_TUNNEL_SPAWNS).
//   * a null ENTRY -> the cell's own trio, in place. Never dropped: entry k belongs to spawn k, so removing
//     one would shift every later spawn onto a different box.
function normalizeSpawns(c) {
    const prev = c.tunnelSpawns;
    if (!Array.isArray(prev)) {
        return { spawns: [], repair: "emptied" };
    }
    if (prev.every(isObject)) {
        return { spawns: prev, repair: "none" }; // same reference: nothing to repair
    }
    return {
        // The colour is left at None rather than taken from the cell: LevelCompiler.TunnelSpawnFor builds
        // exactly this fallback and leaves ColorOverride there, and a colour painted on a tunnel CELL means
        // nothing anyway (its boxes are its spawns).
        spawns: prev.map((s) => (isObject(s) ? s : { group: c.group, sibling: c.sibling, sortOrder: c.sortOrder, colorOverride: MarbleColor.None })),
        repair: "filled",
    };
}
// Returns the template the editor can safely hold, plus what had to be repaired to get there. Returns the
// SAME reference when the template needed nothing — the identity contract every op in templateOps follows,
// so a clean load cannot look like an edit.
export function normalizeTemplate(raw) {
    const repairs = [];
    const note = (message) => {
        repairs.push({ severity: ValidationSeverity.Warning, message, x: -1, y: -1 });
    };
    // `raw` is typed EditorLevel by the cast at the JSON.parse, so at runtime it can be anything a JSON file
    // can hold — including `null`, a number, or a string.
    const wellFormed = isObject(raw);
    if (!wellFormed) {
        note("This file does not hold a template object; an empty 0×0 board was opened instead.");
    }
    let changed = !wellFormed;
    const loose = (wellFormed ? raw : {});
    const columns = boardBound(loose.columns);
    const rows = boardBound(loose.rows);
    if (wellFormed && (columns !== loose.columns || rows !== loose.rows)) {
        note(`Board size was not a pair of whole numbers; it was read as ${columns}×${rows}.`);
        changed = true;
    }
    // `SchemaVersion` defaults to `CurrentSchemaVersion` on the backend model, so a file with no such key is
    // already the current version as far as everything downstream is concerned — writing that down is the
    // same behaviour-neutral repair as the rest of this file. It is not cosmetic: readDraft refuses any
    // draft whose version isn't the current one, so a template carrying `undefined` was perfectly editable,
    // wrote a draft on every edit, and had every one of them silently thrown away on the next load. The
    // crash mat was inert for precisely the older files this door exists to make openable.
    //
    // A number is left ALONE, including one from the future: this repair materialises a default, it does not
    // claim a file is something it says it isn't.
    const schemaVersion = typeof loose.schemaVersion === "number" && Number.isFinite(loose.schemaVersion)
        ? loose.schemaVersion
        : CURRENT_SCHEMA_VERSION;
    if (wellFormed && schemaVersion !== loose.schemaVersion) {
        note(`This template carried no readable schema version; it was read as version ${CURRENT_SCHEMA_VERSION}.`);
        changed = true;
    }
    const generation = isObject(loose.generation) ? loose.generation : null;
    if (!generation) {
        note("This template had no generation parameters; defaults were filled in — check the palette size and seed before saving.");
        changed = true;
    }
    const set = isObject(loose.cells) ? loose.cells : null;
    if (!set) {
        note("This template had no cells block; the board was read as empty.");
        changed = true;
    }
    if (set && !Array.isArray(set.items)) {
        note("The cell list was not an array; the board was read as empty.");
        changed = true;
    }
    if (set && !Array.isArray(set.modifiers)) {
        note("The modifier list was not an array; it was read as empty.");
        changed = true;
    }
    const items = [];
    let droppedCells = 0;
    let filledSpawns = 0;
    let emptiedSpawns = 0;
    for (const c of (set && Array.isArray(set.items) ? set.items : [])) {
        if (!isObject(c)) {
            droppedCells++;
            continue;
        }
        const cell = c;
        const { spawns, repair } = normalizeSpawns(cell);
        if (repair === "none") {
            items.push(cell);
            continue;
        }
        if (repair === "filled") {
            filledSpawns++;
        }
        else {
            emptiedSpawns++;
        }
        items.push({ ...cell, tunnelSpawns: spawns });
    }
    if (droppedCells > 0) {
        note(`Dropped ${droppedCells} empty ${plural(droppedCells, "entry", "entries")} from the cell list.`);
        changed = true;
    }
    if (emptiedSpawns > 0) {
        note(`${emptiedSpawns} ${plural(emptiedSpawns, "cell has", "cells have")} an empty list of per-spawn records; each spawn uses the cell's own group/sibling/sort, as the game already reads them.`);
        changed = true;
    }
    if (filledSpawns > 0) {
        note(`Filled in the per-spawn records on ${filledSpawns} ${plural(filledSpawns, "cell", "cells")} from the cell's own group/sibling/sort — the values the game already used for them.`);
        changed = true;
    }
    // Painted slices are the one override not attached to a cell: a bare list of {wedge, depth, colour}
    // triples that the ops read on every draw. An entry missing any of the three cannot be drawn OR
    // compiled, so it is dropped here — and reported, because a colour vanishing off the grid with no
    // explanation is exactly the kind of silence this door exists to break.
    const rawSlicePaint = loose.sliceColorOverrides;
    let slicePaint = rawSlicePaint;
    if (rawSlicePaint !== undefined && rawSlicePaint !== null) {
        if (!Array.isArray(rawSlicePaint)) {
            slicePaint = [];
            note("The painted-slice list could not be read; those colours are gone and every slice takes the colour of the box that made it.");
            changed = true;
        }
        else {
            const kept = rawSlicePaint.filter(isReadableSlicePaint);
            const dropped = rawSlicePaint.length - kept.length;
            if (dropped > 0) {
                note(`Dropped ${dropped} unreadable painted slice ${plural(dropped, "colour", "colours")}; ${plural(dropped, "that slice takes", "those slices take")} the colour of the box that made ${plural(dropped, "it", "them")}.`);
                changed = true;
                slicePaint = kept;
            }
        }
    }
    // Frozen slices: the same bare list, the same door.
    const rawFrozen = loose.frozenSlices;
    let frozen = rawFrozen;
    if (rawFrozen !== undefined && rawFrozen !== null) {
        if (!Array.isArray(rawFrozen)) {
            frozen = null;
            note("The frozen-slice list could not be read; no slice starts frozen.");
            changed = true;
        }
        else {
            const kept = rawFrozen.filter(isReadableFrozenSlice);
            const dropped = rawFrozen.length - kept.length;
            if (dropped > 0) {
                note(`Dropped ${dropped} unreadable frozen ${plural(dropped, "slice", "slices")}.`);
                changed = true;
                frozen = kept;
            }
        }
    }
    const modifiers = [];
    let droppedMods = 0;
    let emptiedMods = 0;
    let droppedPoints = 0;
    for (const m of (set && Array.isArray(set.modifiers) ? set.modifiers : [])) {
        if (!isObject(m)) {
            droppedMods++;
            continue;
        }
        const mod = m;
        if (!Array.isArray(mod.cells)) {
            // Kept, not dropped — the rule withCellRemoved already follows: a modifier whose cells cannot be
            // read cannot be PROVEN to reference anything, so it is treated as harmless rather than thrown
            // away. An empty list reads as "covers nothing" everywhere downstream, which is exactly how the
            // per-call-site guards treated it.
            emptiedMods++;
            modifiers.push({ ...mod, cells: [] });
            continue;
        }
        if (mod.cells.every(isObject)) {
            modifiers.push(mod);
            continue;
        }
        // An unreadable POINT has no neutral value to stand in for it: Vector2Int is a struct on the backend,
        // so such a file cannot bind there at all (it only reaches us because the editor GETs Drive's raw
        // bytes and parses them itself). Dropping the point leaves a modifier the validator can judge and
        // report — a degenerate link or a 1×1 lock — instead of one that invents a coordinate.
        const cells = mod.cells.filter(isObject);
        droppedPoints += mod.cells.length - cells.length;
        modifiers.push({ ...mod, cells });
    }
    if (droppedMods > 0) {
        note(`Dropped ${droppedMods} empty ${plural(droppedMods, "entry", "entries")} from the modifier list.`);
        changed = true;
    }
    if (emptiedMods > 0) {
        note(`${emptiedMods} ${plural(emptiedMods, "modifier", "modifiers")} had no readable cell list and now cover nothing.`);
        changed = true;
    }
    if (droppedPoints > 0) {
        note(`Dropped ${droppedPoints} unreadable ${plural(droppedPoints, "cell reference", "cell references")} from the modifier list.`);
        changed = true;
    }
    if (!changed) {
        return { template: raw, repairs };
    }
    return {
        template: {
            ...loose,
            schemaVersion,
            name: loose.name,
            columns,
            rows,
            difficulty: loose.difficulty,
            generation: generation ?? defaultGeneration(),
            sliceColorOverrides: slicePaint,
            frozenSlices: frozen,
            cells: { items, modifiers },
        },
        repairs,
    };
}
